// GET /businesses — backend returns both snake_case and camelCase for a
// few fields (external_business_id/externalBusinessId, etc.); we only read
// the snake_case ones below since they're always present alongside the
// duplicate.
export interface BusinessCustomFields {
  org_id?: string | null;
  org_code?: string | null;
  org_name?: string | null;
  org_status?: string | null;
  org_address?: string | null;
  total_units?: number | null;
  vacant_units?: number | null;
  source_system?: string | null;
  access_partner?: string | null;
  last_synced_at?: string | null;
  occupied_units?: number | null;
  total_residents?: number | null;
  active_tag_count?: number | null;
  app_activity_score?: number | null;
  org_onboarded_date?: string | null;
  last_resident_login?: string | null;
  outstanding_balance?: number | null;
  whatsapp_opt_in_rate?: number | null;
  monthly_billed_amount?: number | null;
  total_facility_admins?: number | null;
  last_billing_cycle_date?: string | null;
  payment_compliance_rate?: number | null;
  [key: string]: unknown;
}

export interface BusinessRecord {
  id: string;
  tenant_id?: string;
  name: string;
  email?: string | null;
  phone?: string | null;
  website?: string | null;
  industry?: string | null;
  address?: string | null;
  notes?: string | null;
  external_business_id?: string | null;
  primary_contact_id?: string | null;
  stage?: string | null;
  status?: string | null;
  estimated_value?: number | null;
  tags: string[];
  custom?: BusinessCustomFields | null;
  created_at?: string;
  updated_at?: string;
}

export interface BusinessListResponse {
  results: BusinessRecord[];
  page: number;
  per_page: number;
  total: number;
}

export const getBusinessInitials = (name: string): string =>
  name
    ?.trim()
    ?.split(/\s+/)
    ?.slice(0, 2)
    ?.map((w) => w[0])
    ?.join("")
    ?.toUpperCase() || "?";

export const formatBusinessDate = (value?: string | null): string => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

export const formatBusinessNumber = (value?: number | null): string =>
  value === null || value === undefined ? "—" : value.toLocaleString();

export const getBusinessStatusStyle = (status?: string | null): string => {
  switch ((status || "").toLowerCase()) {
    case "active":
      return "bg-green-50 text-green-700 border border-green-100 dark:bg-green-500/15 dark:text-green-400 dark:border-green-500/30";
    case "disabled":
      return "bg-gray-100 text-gray-600 border border-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600";
    default:
      return "bg-amber-50 text-amber-700 border border-amber-100 dark:bg-amber-500/15 dark:text-amber-400 dark:border-amber-500/30";
  }
};

export const getBusinessStageStyle = (): string =>
  "bg-indigo-50 text-indigo-700 border border-indigo-100 dark:bg-indigo-500/15 dark:text-indigo-400 dark:border-indigo-500/30";
